import { Link, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import logoImg from "@/assets/images/logo.png";
import { useAuth } from "@/hooks/use-auth";
import { useEffect } from "react";
import {
  FileText,
  Shield,
  Smartphone,
  Monitor,
  Users,
  Package,
  CheckCircle,
  ArrowRight,
  ClipboardCheck,
  Camera,
  Building2,
  Wrench,
} from "lucide-react";

const features = [
  {
    icon: ClipboardCheck,
    title: "Laudos de Avaliação",
    description:
      "Crie, gerencie e assine laudos de avaliação veicular com registro completo do estado do veículo.",
  },
  {
    icon: Camera,
    title: "Registro Fotográfico",
    description:
      "Capture e anexe fotos dos elementos estruturais e do veículo diretamente no laudo, garantindo documentação completa da vistoria.",
  },
  {
    icon: Package,
    title: "Elementos Estruturais",
    description:
      "Catálogo completo de elementos estruturais por tipo de veículo (auto leve, moto, caminhão, ônibus) conforme CONTRAN 810/2020.",
  },
  {
    icon: Users,
    title: "Gestão Multi-Equipe",
    description:
      "Controle técnicos de vistoria, responsáveis técnicos com CREA/ART e parceiros de negócios com permissões específicas.",
  },
  {
    icon: Building2,
    title: "Multi-Empresa",
    description:
      "Vistoriadoras, parceiros de negócios e responsáveis técnicos gerenciados em um único sistema integrado.",
  },
  {
    icon: Wrench,
    title: "Laudos de Reclassificação",
    description:
      "Reclassificação de monta conforme a Resolução CONTRAN nº 810/2020 e Portaria SENATRAN nº 587/2024.",
    maintenance: true,
  },
];

const appFeatures = [
  "Realizar vistoria técnica de elementos estruturais no campo",
  "Fotografar elementos e veículos com o celular",
  "Registrar condições e danos de cada elemento",
  "Assinar digitalmente os laudos de avaliação",
  "Receber notificações de novos processos",
];

const webFeatures = [
  "Painel administrativo completo",
  "Gerenciamento de empresas e parceiros",
  "Acompanhar todos os laudos de avaliação",
  "Controle de Responsáveis Técnicos (CREA/ART)",
  "Auditoria e relatórios técnicos",
];

export default function LandingPage() {
  const { user, loading: authLoading } = useAuth();
  const [, setLocation] = useLocation();

  useEffect(() => {
    if (!authLoading && user) {
      setLocation("/dashboard");
    }
  }, [authLoading, user, setLocation]);

  return (
    <div className="min-h-screen bg-white">
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-lg border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src={logoImg}
              alt="ReMonta"
              className="h-10 w-10 object-contain"
            />
            <span className="font-display font-bold text-xl text-slate-900">
              ReMonta
            </span>
          </div>
          <div className="flex items-center gap-3">
            <Link href="/login">
              <Button variant="ghost" data-testid="link-login">
                Entrar
              </Button>
            </Link>
            <Link href="/registrar">
              <Button variant="outline" data-testid="link-registrar-nav">
                Registrar
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      <section className="pt-32 pb-20 px-4 sm:px-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-cyan-50" />
        <div className="absolute top-20 right-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-accent/5 rounded-full blur-3xl" />

        <div className="max-w-6xl mx-auto relative">
          <div className="text-center max-w-3xl mx-auto">
            <div className="flex justify-center mb-8">
              <img
                src={logoImg}
                alt="ReMonta"
                className="h-32 w-32 object-contain drop-shadow-xl"
                data-testid="img-logo-hero"
              />
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold text-slate-900 leading-tight">
              Avaliação e Reclassificação de Monta
              <span className="block text-primary mt-2">Digital e Conforme</span>
            </h1>

            <p className="mt-6 text-lg sm:text-xl text-slate-600 max-w-2xl mx-auto leading-relaxed">
              Plataforma completa para laudos de avaliação veicular.
              Gerencie elementos estruturais, equipes técnicas e parceiros em
              conformidade com a{" "}
              <strong>Resolução CONTRAN nº 810/2020</strong>. Reclassificação
              temporariamente em manutenção.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-10">
              <Link href="/login">
                <Button
                  size="lg"
                  variant="outline"
                  className="gap-2"
                  data-testid="button-hero-login"
                >
                  Acessar Painel Web
                  <ArrowRight className="w-5 h-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 px-4 sm:px-6 bg-slate-50" id="funcionalidades">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-primary font-semibold text-sm uppercase tracking-wider mb-2">
              Funcionalidades
            </p>
            <h2 className="text-3xl sm:text-4xl font-display font-bold text-slate-900">
              Tudo que você precisa em um só lugar
            </h2>
            <p className="mt-4 text-slate-600 max-w-2xl mx-auto">
              Do campo ao escritório, o ReMonta conecta toda a cadeia de
              avaliação e reclassificação veicular.
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {features.map((feature, i) => (
              <Card
                key={i}
                className="border-none shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1 bg-white"
                data-testid={`card-feature-${i}`}
              >
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                    <feature.icon className="w-6 h-6 text-primary" />
                  </div>
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="font-display font-bold text-lg text-slate-900">
                      {feature.title}
                    </h3>
                    {feature.maintenance && (
                      <Badge variant="secondary" className="bg-amber-100 text-amber-700">
                        Em manutenção
                      </Badge>
                    )}
                  </div>
                  <p className="text-slate-600 text-sm leading-relaxed">
                    {feature.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-primary font-semibold text-sm uppercase tracking-wider mb-2">
              App + Web
            </p>
            <h2 className="text-3xl sm:text-4xl font-display font-bold text-slate-900">
              Dois ambientes, uma plataforma
            </h2>
            <p className="mt-4 text-slate-600 max-w-2xl mx-auto">
              O app mobile para o trabalho em campo e o painel web para a gestão
              administrativa.
            </p>
          </div>

          <div className="grid gap-8 md:grid-cols-2">
            <Card className="border-none shadow-lg bg-gradient-to-br from-blue-600 to-blue-700 text-white overflow-hidden relative">
              <div className="absolute top-0 right-0 w-40 h-40 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
              <CardContent className="p-8 relative">
                <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center mb-6">
                  <Smartphone className="w-7 h-7" />
                </div>
                <h3 className="font-display font-bold text-2xl mb-2">
                  App Mobile
                </h3>
                <p className="text-blue-100 mb-6">
                  Para técnicos de vistoria, responsáveis técnicos e parceiros
                  atuarem no campo.
                </p>
                <ul className="space-y-3">
                  {appFeatures.map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-sm">
                      <CheckCircle className="w-4 h-4 text-blue-200 flex-shrink-0" />
                      <span className="text-blue-50">{item}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-6 flex items-center gap-2">
                  <Shield className="w-4 h-4 text-blue-200" />
                  <span className="text-xs text-blue-200">Android</span>
                </div>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg bg-gradient-to-br from-slate-800 to-slate-900 text-white overflow-hidden relative">
              <div className="absolute top-0 right-0 w-40 h-40 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
              <CardContent className="p-8 relative">
                <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center mb-6">
                  <Monitor className="w-7 h-7" />
                </div>
                <h3 className="font-display font-bold text-2xl mb-2">
                  Painel Web
                </h3>
                <p className="text-slate-300 mb-6">
                  Para administradores e gestores controlarem toda a operação.
                </p>
                <ul className="space-y-3">
                  {webFeatures.map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-sm">
                      <CheckCircle className="w-4 h-4 text-slate-400 flex-shrink-0" />
                      <span className="text-slate-200">{item}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-6">
                  <Link href="/login">
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-white/20 text-white bg-transparent gap-2"
                      data-testid="button-painel-login"
                    >
                      Acessar Painel
                      <ArrowRight className="w-4 h-4" />
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <section className="py-20 px-4 sm:px-6 bg-slate-50" id="quem-usa">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-primary font-semibold text-sm uppercase tracking-wider mb-2">
              Para quem
            </p>
            <h2 className="text-3xl sm:text-4xl font-display font-bold text-slate-900">
              Quem usa o ReMonta?
            </h2>
          </div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {[
              {
                role: "Vistoriadoras",
                desc: "Realizam os laudos de avaliação e gerenciam equipes técnicas de campo.",
                icon: Building2,
                color: "bg-blue-100 text-blue-700",
              },
              {
                role: "Técnicos de Vistoria",
                desc: "Profissionais que vão a campo, inspecionam elementos estruturais e preenchem laudos.",
                icon: ClipboardCheck,
                color: "bg-amber-100 text-amber-700",
              },
              {
                role: "Responsáveis Técnicos",
                desc: "Engenheiros (CREA/ART) que revisam, avaliam e assinam os laudos.",
                icon: Shield,
                color: "bg-purple-100 text-purple-700",
              },
              {
                role: "Solicitantes",
                desc: "Empresas ou profissionais que solicitam avaliações para seus veículos.",
                icon: Users,
                color: "bg-emerald-100 text-emerald-700",
              },
            ].map((item, i) => (
              <Card
                key={i}
                className="border-none shadow-md text-center bg-white"
                data-testid={`card-role-${i}`}
              >
                <CardContent className="p-6">
                  <div
                    className={`w-14 h-14 rounded-2xl mx-auto mb-4 flex items-center justify-center ${item.color}`}
                  >
                    <item.icon className="w-7 h-7" />
                  </div>
                  <h3 className="font-display font-bold text-base text-slate-900 mb-2">
                    {item.role}
                  </h3>
                  <p className="text-slate-600 text-sm">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 bg-gradient-to-r from-blue-600 to-blue-700 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-display font-bold mb-4">
            Pronto para modernizar suas avaliações?
          </h2>
          <p className="text-blue-100 max-w-xl mx-auto mb-8">
            Emita laudos digitais em conformidade com a CONTRAN 810/2020.
            Controle sua equipe, responsáveis técnicos e parceiros em uma
            plataforma segura.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link href="/login">
              <Button
                size="lg"
                variant="outline"
                className="border-white/30 text-white bg-transparent gap-2"
                data-testid="button-cta-login"
              >
                Acessar Painel Web
                <ArrowRight className="w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <footer className="py-10 px-4 sm:px-6 bg-slate-900 text-white">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img
              src={logoImg}
              alt="ReMonta"
              className="h-8 w-8 object-contain"
            />
            <span className="font-display font-bold">ReMonta</span>
            <span className="text-slate-500 text-sm">|</span>
            <span className="text-slate-400 text-sm">Avaliação e Reclassificação de Monta</span>
          </div>
          <p className="text-slate-500 text-sm">
            Res. CONTRAN nº 810/2020 · Portaria SENATRAN nº 587/2024
          </p>
        </div>
      </footer>
    </div>
  );
}
